import { useEffect } from "react";

import * as Sentry from "@sentry/react";
import {
  reactRouterV6BrowserTracingIntegration,
  type ErrorEvent,
  type EventHint,
} from "@sentry/react";
import { AxiosError } from "axios";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import invariant from "tiny-invariant";

import { isChunkError } from "@/shared/misc/chunkReload";

type ErrorWithSentry = Error & { sentryEventId?: string };

const normalizeUrlPath = (url: string): string => {
  try {
    const { pathname } = new URL(url);
    return pathname
      .replace(/\/[0-9a-f-]{20,}/gi, "/:id")
      .replace(/\/\d+/g, "/:id");
  } catch {
    return url;
  }
};

const isNonActionableAxiosError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  if (
    error.code === AxiosError.ERR_NETWORK ||
    error.code === AxiosError.ERR_CANCELED ||
    error.code === AxiosError.ECONNABORTED ||
    error.code === AxiosError.ETIMEDOUT
  ) {
    return true;
  }

  const status = error.response?.status;
  if (
    status === 401 ||
    status === 403 ||
    status === 404 ||
    (status && status >= 500)
  ) {
    return true;
  }

  return false;
};

const dropNonActionable = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => {
  const error = hint.originalException;

  if (!(error instanceof Error) && typeof error !== "undefined") {
    return null;
  }

  if (isChunkError(error) || isNonActionableAxiosError(error)) {
    return null;
  }

  if (error instanceof AxiosError && error.response?.status) {
    const path = normalizeUrlPath(error.config?.url ?? "");
    event.fingerprint = ["axios-error", String(error.response.status), path];
  }

  return event;
};

const environment = process.env.ENVIRONMENT;
invariant(environment);

class SentryLogger {
  constructor(private environment: string) {
    this.setUser = this.setUser.bind(this);
    this.log = this.log.bind(this);
    this.error = this.error.bind(this);
    this.isRemoteLoggingEnabled = this.isRemoteLoggingEnabled.bind(this);

    if (this.isRemoteLoggingEnabled()) {
      Sentry.init({
        // when SENTRY_DSN is not set, the SDK will not be initialized
        // and that is ok on dev environments
        dsn: process.env.SENTRY_DSN,
        environment: this.environment,
        ignoreErrors: [
          /Failed to fetch/i,
          /NetworkError/i,
          /Load failed/i,
          "Request aborted",
        ],
        denyUrls: [/api\.maptiler\.com/i],
        integrations: [
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
            // keep credential / PII inputs masked even with global unmask (GDPR)
            mask: ['input[type="password"]', 'input[type="email"]'],
          }),
          reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          }),
        ],
        beforeSend: dropNonActionable,
        normalizeDepth: 10,
        release: process.env.RELEASE_ID,
        replaysOnErrorSampleRate: 1.0,
        replaysSessionSampleRate: 0.01,
        sendDefaultPii: false,
        // anchor to host (^https?://...) so the domain can't match when it
        // appears inside a query string — e.g. Mixpanel's /record/ URL embeds
        // $current_url=https://app.cleverfarm.cz/... which otherwise matches and
        // gets a baggage header Mixpanel's CORS rejects
        tracePropagationTargets: [
          /^https?:\/\/(?:[^/]*\.)?cleverfarm\.cz(?=[:/]|$)/,
          /^https?:\/\/(?:[^/]*\.)?cleverfarm\.ag(?=[:/]|$)/,
          /^https?:\/\/(?:[^/]*\.)?mymex\.cz(?=[:/]|$)/,
          /^https?:\/\/(?:[^/]*\.)?agronutriconsult\.sk(?=[:/]|$)/,
          /^https?:\/\/(?:[^/]*\.)?farma\.spzo\.cz(?=[:/]|$)/,
          /^\//, // matches anything on the same URL
        ],
        tracesSampleRate: 0.25,
      });
    }
  }

  public setContext(context: Parameters<typeof Sentry.setContext>[1]) {
    if (!this.isRemoteLoggingEnabled()) {
      return;
    }

    Sentry.setContext("details", context);
  }

  public setUser(user: Sentry.User | null) {
    if (!this.isRemoteLoggingEnabled()) {
      return;
    }
    Sentry.setUser(user);
  }

  public log(...args: unknown[]) {
    // oxlint-disable-next-line no-console
    console.log(...args);
  }

  public warn(message: string, context?: Record<string, unknown>) {
    // oxlint-disable-next-line no-console
    console.warn(message, context);

    if (this.isRemoteLoggingEnabled()) {
      return Sentry.captureMessage(message, {
        level: "warning",
        extra: context,
      });
    }
  }

  public error(e: Error | unknown | undefined, ...args: unknown[]) {
    // oxlint-disable-next-line no-console
    console.error(e, ...args);

    if (this.isRemoteLoggingEnabled()) {
      const eventId = Sentry.captureException(e);
      if (e instanceof Error) {
        (e as ErrorWithSentry).sentryEventId = eventId;
      }
      return eventId;
    }
  }

  private isRemoteLoggingEnabled() {
    return window.location.hostname !== "localhost";
  }
}

const logger = new SentryLogger(environment);

export { logger, dropNonActionable, type ErrorWithSentry };
